/** Notes data hooks — spec §5-F6, §7.5. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NoteScope, PublicNote } from '@virtuetasks/types';
import { api, qs } from '@/lib/api';
import type { TaskView } from '@/features/tasks/api';

export type NoteView = PublicNote;

export interface NoteFilters {
  scope?: NoteScope;
  projectId?: string;
  taskId?: string;
  q?: string;
  tag?: string;
  parentNoteId?: string;
}

export function useNotes(filters: NoteFilters = {}) {
  return useQuery({
    queryKey: ['notes', filters],
    queryFn: async () => (await api.get<NoteView[]>(`/notes${qs({ ...filters, limit: 200 })}`)).data,
  });
}

export function useNote(id?: string) {
  return useQuery({
    queryKey: ['note', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<NoteView>(`/notes/${id}`)).data,
  });
}

export interface NoteVersion {
  _id: string;
  version: number;
  title: string;
  bodyText: string;
  createdAt: string;
  authorId: string;
}

export function useNoteVersions(id?: string) {
  return useQuery({
    queryKey: ['note-versions', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<NoteVersion[]>(`/notes/${id}/versions`)).data,
  });
}

export interface CreateNoteInput {
  scope: NoteScope;
  title: string;
  body?: Record<string, unknown>;
  projectId?: string | null;
  taskId?: string | null;
  parentNoteId?: string | null;
  attendeeIds?: string[];
  meetingDate?: string | null;
  isPinned?: boolean;
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateNoteInput) => (await api.post<NoteView>('/notes', input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notes'] }),
  });
}

export function useUpdateNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<CreateNoteInput>) =>
      (await api.patch<NoteView>(`/notes/${id}`, patch)).data,
    onSuccess: (note) => {
      qc.setQueryData(['note', id], note);
      void qc.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/notes/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notes'] }),
  });
}

export function useTogglePin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, pinned }: { id: string; pinned: boolean }) =>
      (await api.post<NoteView>(`/notes/${id}/${pinned ? 'unpin' : 'pin'}`)).data,
    onSuccess: (note) => {
      qc.setQueryData(['note', note._id], note);
      void qc.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export function useRestoreVersion(noteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (version: number) =>
      (await api.post<NoteView>(`/notes/${noteId}/versions/${version}/restore`)).data,
    onSuccess: (note) => {
      qc.setQueryData(['note', noteId], note);
      void qc.invalidateQueries({ queryKey: ['note-versions', noteId] });
    },
  });
}

export interface ConvertToTaskInput {
  text: string;
  projectId: string;
  assigneeId?: string | null;
  dueDate?: string | null;
  priority?: 'P0' | 'P1' | 'P2' | 'P3';
}

/** F6's highest-value feature: select a line, get a task with a backlink. */
export function useConvertToTask(noteId: string) {
  const qc = useQueryClient();
  return useMutation({
    // The endpoint returns both sides of the link: the updated note and the
    // new task. Callers want the task.
    mutationFn: async (input: ConvertToTaskInput) =>
      (await api.post<{ note: NoteView; task: TaskView }>(
        `/notes/${noteId}/convert-to-task`,
        input,
      )).data.task,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['note', noteId] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useDailyNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.get<NoteView>('/notes/daily/today')).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notes'] }),
  });
}

export function useReorderNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: { noteId: string; order: number; parentNoteId?: string | null }[]) =>
      api.patch('/notes/reorder', { items }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notes'] }),
  });
}
